import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import Grid from "@material-ui/core/Grid";
import { Button, Typography, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { alertActions } from "_store";
import {
  fileExtension,
  fileSizeReadable,
  fileTypeAcceptable,
} from "_utils";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { materialsymbolsupload, materialsymbolsdownload } from "images";
import { wg_report_logo } from "images";

const REQUIRED_HEADERS = ["Marketer Name", "Percent of System Load"];

const StorageTrackingUpload = ({
  rows = [],
  setRows,
  setIsDataChanged,
  supportedFormats = [".xlsx", ".xls"],
  isAdmin,
}) => {
  const header = "Storage Tracking";
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [fileNames, setFileNames] = useState([]);

  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
  });

  const handleError = (message) => {
    dispatch(alertActions.error({ message, header }));
  };

  // ─── Download sample template ────────────────────────────────────────────
  const downloadSample = async () => {
    dispatch(alertActions.clear());

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Storage Tracking");

    // Logo
    try {
      const response = await fetch(wg_report_logo);
      const imageBlob = await response.blob();
      const base64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(imageBlob);
      });
      const imageId = workbook.addImage({ base64, extension: "png" });
      worksheet.addImage(imageId, {
        tl: { col: 3, row: 0 },
        ext: { width: 170, height: 60 },
      });
    } catch (_) {
      // non-fatal
    }

    worksheet.getRow(1).height = 65;
    worksheet.mergeCells("A1:D1");
    const titleCell = worksheet.getCell("A1");
    titleCell.value = "Storage Tracking";
    titleCell.font = { bold: true, size: 16 };
    titleCell.alignment = { horizontal: "left", vertical: "middle" };

    worksheet.addRow([]);

    const dataRows =
      rows.length > 0
        ? rows.map((r) => ({
            "Marketer Name": String(r.MarketerName || ""),
            "Percent of System Load": r.percentOfSystemLoad || "0",
          }))
        : [{ "Marketer Name": "test", "Percent of System Load": "0" }];

    const headerRow = worksheet.addRow(REQUIRED_HEADERS);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF0F3557" },
      };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    dataRows.forEach((rowData) => {
      const row = worksheet.addRow(REQUIRED_HEADERS.map((col) => rowData[col] ?? ""));
      row.eachCell((cell) => {
        cell.alignment = { horizontal: "center" };
      });
    });

    REQUIRED_HEADERS.forEach((col, i) => {
      const maxLen = Math.max(
        col.length,
        ...dataRows.map((r) => String(r[col] ?? "").length)
      );
      worksheet.getColumn(i + 1).width = Math.max(maxLen + 2, 14);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "StorageTracking.xlsx"
    );
  };

  // ─── Apply uploaded sheet data to table rows ─────────────────────────────
  const applySheet = (jsonData) => {
    if (!jsonData || jsonData.length === 0) {
      handleError("The uploaded sheet contains no data.");
      return false;
    }

    const firstRowKeys = Object.keys(jsonData[0] || {});
    const hasRequiredKeys = REQUIRED_HEADERS.every((h) => firstRowKeys.includes(h));
    if (!hasRequiredKeys) {
      handleError(
        `The uploaded sheet does not match the required format. Expected columns: ${REQUIRED_HEADERS.join(", ")}.`
      );
      return false;
    }

    const nonEmptyRows = jsonData.filter((row) =>
      Object.values(row).some((v) => String(v).trim() !== "")
    );
    if (nonEmptyRows.length === 0) {
      handleError("The uploaded sheet contains no data rows.");
      return false;
    }

    const uploadMap = {};
    const invalidMarketers = [];

    nonEmptyRows.forEach((row) => {
      const marketerName = String(row["Marketer Name"] || "").trim();
      if (!marketerName) return;

      const matchedRow = rows.find(
        (r) => String(r.MarketerName).trim().toLowerCase() === marketerName.toLowerCase()
      );

      if (!matchedRow) {
        invalidMarketers.push(marketerName);
        return;
      }

      const rawVal = row["Percent of System Load"];
      const numericVal = String(rawVal).replace(/[^0-9.]/g, "");
      uploadMap[marketerName.toLowerCase()] = numericVal || "0";
    });

    if (Object.keys(uploadMap).length === 0) {
      handleError("None of the Marketer Names in the uploaded file matched the current table.");
      return false;
    }

    setRows((prev) =>
      prev.map((row) => {
        const key = String(row.MarketerName).trim().toLowerCase();
        if (!(key in uploadMap)) return row;
        return { ...row, percentOfSystemLoad: uploadMap[key] };
      })
    );

    if (invalidMarketers.length > 0) {
      dispatch(
        alertActions.error({
          message: `${invalidMarketers.length} row(s) were skipped because their Marketer Names do not exist in the current view: ${invalidMarketers.join(", ")}`,
          header,
        })
      );
    }

    return true;
  };

  // ─── Main file-change handler ─────────────────────────────────────────────
  const handleChange = async (event) => {
    dispatch(alertActions.clear());
    event.preventDefault();

    const filesAdded = event.dataTransfer
      ? event.dataTransfer.files
      : event.target.files;

    if (!filesAdded || filesAdded.length === 0) return;

    const file = filesAdded[0];
    const MAX_FILE_SIZE_MB = 10;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    file.extension = fileExtension(file);
    file.sizeReadable = fileSizeReadable(file.size);

    if (file.size > MAX_FILE_SIZE_BYTES) {
      handleError(`File size cannot exceed ${MAX_FILE_SIZE_MB} MB`);
      return;
    }

    if (!fileTypeAcceptable(supportedFormats, file)) {
      handleError("Invalid file format. Only Excel files are supported.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        // The template has a title row and a blank row above the real header
        // row, so locate the header row instead of assuming it's row 1.
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
        const headerRowIndex = rawRows.findIndex((row) =>
          row.some((cell) => String(cell).trim().toLowerCase() === "marketer name")
        );

        if (headerRowIndex === -1) {
          handleError(
            `The uploaded sheet does not match the required format. Expected columns: ${REQUIRED_HEADERS.join(", ")}.`
          );
          return;
        }

        const jsonData = XLSX.utils.sheet_to_json(sheet, {
          range: headerRowIndex,
          defval: "",
          raw: false,
        });

        const applied = applySheet(jsonData);

        if (applied) {
          setIsDataChanged(true);
          setFileNames([file.name]);
        }
      } catch (err) {
        handleError("An unexpected error occurred while reading the file.");
        console.error(err);
      }
    };

    reader.readAsArrayBuffer(file);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Typography component="div" className="UploadContainer">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Button
            component="label"
            variant="contained"
            disabled={!isAdmin}
            className="Uploadfiles"
            startIcon={<img src={materialsymbolsupload} alt="Upload" />}
          >
            <span>Upload your files here</span>
            <VisuallyHiddenInput
              type="file"
              onChange={handleChange}
              ref={fileInputRef}
            />
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Typography component="div" className="SupportedFormats">
            <Typography component="h3">Supported Formats</Typography>
            <Typography component="div" className="fileformatlist">
              {supportedFormats.map((fmt) => (
                <span key={fmt}>{fmt} </span>
              ))}
            </Typography>
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography component="div" className="SupportedFormats">
            <Typography component="h3">Download Template</Typography>
            <div className="mar-top-16">
              <Typography component="div">
                Sample File Format
                <IconButton onClick={downloadSample}>
                  <img
                    src={materialsymbolsdownload}
                    alt="material-symbols_download"
                  />
                </IconButton>
              </Typography>
            </div>
          </Typography>
        </Grid>

        {fileNames.length > 0 && (
          <Grid item xs={12}>
            <Typography component="div" className="SupportedFormats">
              <Typography component="h3">Uploaded File</Typography>
              {fileNames.map((name) => (
                <Typography key={name} component="div" style={{ fontSize: 13 }}>
                  {name}
                </Typography>
              ))}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Typography>
  );
};

export default StorageTrackingUpload;
